import { t, Trans } from "@lingui/macro";
import { Box, Menu, MenuItem, Container } from "@mui/material";
import { useCallback, useState } from "react";
import useEditMaps from "./useEditMaps";
import { useNavigate } from "react-router";
import useTilePackNames from "./useTilePackNames";
import useNavUpOnDeviceDisconnect from "../useNavUpOnDeviceDisconnect";
import MapRegionEmbed from "./MapRegionEmbed";
import { WSMBoltTilePack } from "@WahooFitness/wsm-native/dist/esm/types/bolt_map";
import { WfIconNavigationOverflowRegular } from "@WahooFitness/redesignr";
import { GlobalHeader } from "@WahooFitness/wahoo-offline-mfe";

const MapRegion = ({ appToken, tilePackId }: { appToken: string; tilePackId: number }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const openMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  useNavUpOnDeviceDisconnect(appToken);

  const [tilePacks, setTilePacks] = useState<WSMBoltTilePack[]>([]);

  const {
    editing,
    onDeleteClick,
    disableEditing,
    enableEditing,
    handleMapSelect,
    pendingDeleteTilePackIds,
  } = useEditMaps(appToken, tilePacks);

  const handleEditClick = useCallback(() => {
    setMenuAnchorEl(null);
    enableEditing();
  }, [enableEditing]);

  const { tilePackNameTranslations } = useTilePackNames();

  const navigate = useNavigate();
  const handleUpdateAllClick = useCallback(() => {
    navigate(`/device-config/${appToken}/maps/update`);
    closeMenu();
  }, [appToken, closeMenu, navigate]);

  const handleTilePackLoad = useCallback((tilePack: WSMBoltTilePack) => {
    setTilePacks(tilePack.tilePacks || []);
  }, []);

  return (
    <>
      <GlobalHeader
        title={tilePackNameTranslations[tilePackId]}
        primaryLeftActionItem={
          editing
            ? {
                text: t`Done`,
                action: disableEditing,
              }
            : undefined
        }
        primaryRightActionItem={
          editing
            ? {
                text: t`Delete`,
                action: onDeleteClick,
              }
            : {
                icon: <WfIconNavigationOverflowRegular />,
                action: openMenu,
              }
        }
      />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <Box
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          p={2}
          justifyContent="space-between"
        >
          <MapRegionEmbed
            appToken={appToken}
            tilePackId={tilePackId}
            onTilePackLoad={handleTilePackLoad}
            editing={editing}
            handleMapSelect={handleMapSelect}
            pendingDeleteTilePackIds={pendingDeleteTilePackIds}
          />
          <Menu open={menuOpen} onClose={closeMenu} anchorEl={menuAnchorEl}>
            <MenuItem onClick={handleEditClick}>
              <Trans>Edit</Trans>
            </MenuItem>
            <MenuItem onClick={handleUpdateAllClick}>
              <Trans>Update maps</Trans>
            </MenuItem>
          </Menu>
        </Box>
      </Container>
    </>
  );
};

export default MapRegion;
